/**
 * The value of a position, rotation or scale track of an Animation at a time: `Animation::_interpolate`
 * (`scene/resources/animation.cpp:2524-2726`) for a playhead that moves forward, which is how the
 * mixer samples a track it applies (`animation_mixer.cpp:1373-1380`). Key times, transitions and
 * values are `real_t`, so a caller rounds them to float32 first.
 */
import { clamp, cubicInterpolateInTimef, ease, isEqualApprox, isZeroApprox, lerpf, pingpongf, posmod, round, } from '../math/math.js';
import { storedReal as f } from '../math/real.js';
import { quaternionSlerp } from '../math/quaternion.js';
import { quaternionSphericalCubicInterpolateInTime } from '../math/quaternionCubic.js';
import { vector3CubicInterpolateInTime, vector3Lerp } from '../math/vector.js';
import { AnimationInterpolation, AnimationLoopMode } from './animation.js';
const VECTOR3_BLEND = { lerp: vector3Lerp, cubic: vector3CubicInterpolateInTime };
const QUATERNION_BLEND = {
    lerp: quaternionSlerp,
    cubic: quaternionSphericalCubicInterpolateInTime,
};
/** A Color or a vector of any size, as its flat components: each moves on its own, as `Animation` blends a Variant. */
const TUPLE_BLEND = {
    lerp: (from, to, weight) => from.map((component, i) => lerpf(component, to[i] ?? component, weight)),
    cubic: (from, to, pre, post, weight, { toT, preT, postT }) => from.map((component, i) => cubicInterpolateInTimef(component, to[i] ?? component, pre[i] ?? component, post[i] ?? component, weight, toT, preT, postT)),
};
/** The value of a `value` track of Colors or vectors at `time` (`value_track_interpolate`), or null where `Animation` has none. */
export function sampleTupleTrack(keys, time, setup) {
    return sampleTrack(keys, time, setup, TUPLE_BLEND);
}
/** The value of a position or scale track at `time`, or null where `Animation` has none to give. */
export function sampleVector3Track(keys, time, setup) {
    return sampleTrack(keys, time, setup, VECTOR3_BLEND);
}
/** The value of a rotation track at `time`, or null where `Animation` has none to give. */
export function sampleQuaternionTrack(keys, time, setup) {
    return sampleTrack(keys, time, setup, QUATERNION_BLEND);
}
function sampleTrack(keys, time, setup, blend) {
    // The last key within the length counts, and keys past it do not (`:2525`).
    const count = findKey(keys, setup.length) + 1;
    if (count <= 0)
        return null;
    if (count === 1)
        return keys[0].value;
    const span = spanAt(keys, count, time, setup);
    const { idx, next } = span;
    const c = isZeroApprox(span.delta) ? 0 : f(span.from / span.delta);
    const key = keys[idx];
    if (key.transition === 0)
        return key.value;
    const eased = key.transition === 1 ? c : f(ease(c, key.transition));
    switch (setup.interp) {
        case AnimationInterpolation.LINEAR:
        case AnimationInterpolation.LINEAR_ANGLE:
            return blend.lerp(key.value, keys[next].value, eased);
        case AnimationInterpolation.CUBIC:
        case AnimationInterpolation.CUBIC_ANGLE:
            return blend.cubic(key.value, keys[next].value, keys[span.pre].value, keys[span.post].value, eased, cubicTimes(keys, span, setup));
        default:
            return key.value;
    }
}
/** `span` with the time the playhead is past `idx` and the time from `idx` to `next`, which it owns. */
function withTimes(span, delta, from) {
    span.delta = delta;
    span.from = from;
    return span;
}
/**
 * `Animation::_find` (`animation.cpp:2404-2454`) without a limit: the index of the last key at or
 * before `time`, -1 before the first, and -2 for no key.
 */
function findKey(keys, time) {
    if (keys.length === 0)
        return -2;
    let low = 0;
    let high = keys.length - 1;
    let middle = 0;
    while (low <= high) {
        middle = Math.trunc((low + high) / 2);
        const keyTime = keys[middle].time;
        if (isEqualApprox(time, keyTime))
            return middle;
        if (time < keyTime)
            high = middle - 1;
        else
            low = middle + 1;
    }
    return keys[middle].time > time ? middle - 1 : middle;
}
/** Whether the span past the last key runs on to the first: the track wraps and the Animation loops. */
function wrapsAround({ loopWrap, loopMode }) {
    return loopWrap && loopMode !== AnimationLoopMode.NONE;
}
function spanAt(keys, count, time, setup) {
    const found = findKey(keys, time);
    const isStartEdge = found === -1;
    const isEndEdge = found >= count - 1;
    let span;
    if (!wrapsAround(setup))
        span = spanWithoutLoop(found, isStartEdge, count);
    else if (setup.loopMode === AnimationLoopMode.LINEAR) {
        span = spanAroundLoop(keys, found, isStartEdge, isEndEdge, count, time, setup);
    }
    else
        span = spanAcrossPingPong(keys, found, isStartEdge, isEndEdge, count, time, setup);
    if (!isStartEdge && !isEndEdge) {
        const start = keys[span.idx].time;
        return withTimes(span, f(keys[span.next].time - start), f(time - start));
    }
    return span;
}
/** `LOOP_NONE`, or a track that does not wrap: both ends hold their key (`:2559-2566`). */
function spanWithoutLoop(found, isStartEdge, count) {
    const idx = isStartEdge ? 0 : found;
    const last = count - 1;
    return {
        idx,
        next: clamp(idx + 1, 0, last),
        pre: clamp(idx - 1, 0, last),
        post: clamp(idx + 2, 0, last),
        from: 0,
        delta: 0,
    };
}
/** `LOOP_LINEAR`: the span past the last key runs to the first, and the first key's span starts at the last (`:2567-2594`). */
function spanAroundLoop(keys, found, isStartEdge, isEndEdge, count, time, setup) {
    const idx = isStartEdge ? count - 1 : found;
    const next = posmod(idx + 1, count);
    const span = {
        idx,
        next,
        pre: posmod(idx - 1, count),
        post: posmod(idx + 2, count),
        from: 0,
        delta: 0,
    };
    const idxTime = keys[idx].time;
    const nextTime = keys[next].time;
    if (isStartEdge) {
        const endTime = Math.max(f(setup.length - idxTime), 0);
        return withTimes(span, f(endTime + nextTime), f(endTime + time));
    }
    if (isEndEdge) {
        return withTimes(span, f(f(setup.length - idxTime) + nextTime), f(time - idxTime));
    }
    return span;
}
/** `LOOP_PINGPONG`: the keys fold back at both ends (`:2595-2625`). */
function spanAcrossPingPong(keys, found, isStartEdge, isEndEdge, count, time, setup) {
    const foldedIndex = (index) => round(pingpongf(f(index + 0.5), count) - 0.5);
    const walked = isStartEdge ? -1 : found;
    const idx = foldedIndex(walked);
    const next = foldedIndex(walked + 1);
    const span = {
        idx,
        next,
        pre: foldedIndex(walked - 1),
        post: foldedIndex(walked + 2),
        from: 0,
        delta: 0,
    };
    const idxTime = keys[idx].time;
    const nextTime = keys[next].time;
    if (isStartEdge) {
        const endTime = Math.max(idxTime, 0);
        return withTimes(span, f(endTime + nextTime), f(endTime + time));
    }
    if (isEndEdge) {
        return withTimes(span, f(f(f(setup.length * 2) - idxTime) - nextTime), f(time - idxTime));
    }
    return span;
}
function timeAt(keys, index) {
    return keys[index].time;
}
/** The times of `next`, `pre` and `post` counted from `idx`, wrapped as the loop mode wraps them (`:2693-2714`). */
function cubicTimes(keys, { idx, next, pre, post }, setup) {
    const start = timeAt(keys, idx);
    if (!wrapsAround(setup)) {
        return {
            preT: f(timeAt(keys, pre) - start),
            toT: f(timeAt(keys, next) - start),
            postT: f(timeAt(keys, post) - start),
        };
    }
    const { length } = setup;
    if (setup.loopMode === AnimationLoopMode.LINEAR) {
        return {
            preT: pre > idx ? f(f(-length + timeAt(keys, pre)) - start) : f(timeAt(keys, pre) - start),
            toT: next < idx ? f(f(length + timeAt(keys, next)) - start) : f(timeAt(keys, next) - start),
            postT: next < idx || post <= idx ? f(f(length + timeAt(keys, post)) - start) : f(timeAt(keys, post) - start),
        };
    }
    return pingPongCubicTimes(keys, { idx, next, pre, post }, length);
}
/** The times of the cubic where the keys fold back, which reflect the span across the end that holds the playhead. */
function pingPongCubicTimes(keys, { idx, next, pre, post }, length) {
    const start = timeAt(keys, idx);
    let preT = f(timeAt(keys, pre) - start);
    let toT = f(timeAt(keys, next) - start);
    let postT = f(timeAt(keys, post) - start);
    if ((pre > idx && idx === next && post < next) || (pre < idx && idx === next && post > next)) {
        preT = f(start - timeAt(keys, pre));
    }
    else if (pre === idx) {
        preT = idx < next ? f(f(-start) * 2) : f(f(length - start) * 2);
    }
    if (idx === next) {
        toT = pre < idx ? f(f(length - start) * 2) : f(f(-start) * 2);
        postT = f(f(timeAt(keys, next) - timeAt(keys, post)) + toT);
    }
    else if (next === post) {
        postT =
            idx < next ? f(f(f(length - timeAt(keys, next)) * 2) + toT) : f(f(f(-timeAt(keys, next)) * 2) + toT);
    }
    return { preT, toT, postT };
}
//# sourceMappingURL=animationTrackSampler.js.map